import React, { memo, useEffect } from 'react';
import { useQueryParams } from 'hooks';
import SortBy from 'components/common/SortBy';

export interface ISortFilterProps {
  disabled?: boolean;
  defaultOrder?: string;
  type?: 'events' | 'alphabetical' | 'pastEvents' | 'expiration' | 'organizations';
}

const toStr = (v: unknown): string | undefined => {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v[0];
  return undefined;
};

const SortFilter: React.FC<React.PropsWithChildren<ISortFilterProps>> = (props) => {
  const { disabled, defaultOrder, type = 'events' } = props;
  const [queryParams, setQueryParams] = useQueryParams();

  const orderingFromQuery = toStr(queryParams.ordering);

  const effectiveOrdering = orderingFromQuery ?? defaultOrder ?? 'session_date';

  useEffect(() => {
    const currentOrdering = toStr(queryParams.ordering);
    let nextOrdering = currentOrdering;

    if (type === 'pastEvents') {
      if (currentOrdering !== 'start_at' && currentOrdering !== '-start_at') {
        nextOrdering = '-start_at';
      }
    } else if (!currentOrdering) {
      nextOrdering = defaultOrder ?? 'session_date';
    }

    if (nextOrdering !== currentOrdering) {
      setQueryParams(
        {
          ...queryParams,
          ordering: nextOrdering,
        },
        { replace: true },
      );
    }
  }, [type, defaultOrder, queryParams.ordering, setQueryParams, queryParams]);

  const handleChangeOrder = (order: string, orderBy: string) => {
    if (type === 'pastEvents') {
      const current = orderingFromQuery ?? defaultOrder ?? '-start_at';
      const next = current === '-start_at' ? 'start_at' : '-start_at';

      setQueryParams({
        ...queryParams,
        ordering: next,
      });

      return;
    }

    setQueryParams({
      ...queryParams,
      ordering: `${order}${orderBy}`,
    });
  };

  return <SortBy disabled={disabled} sorting={effectiveOrdering} type={type} onChange={handleChangeOrder} />;
};

export default memo(SortFilter);
